import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { PlusIcon } from "lucide-react";
import Modal from "../components/Modal";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import useRoleManagementStore from "../stores/roleManagementStore";

const transformedRolesData = (roles, filterTenantId = null) => {
  // Filter roles by tenantId if provided (for SuperAdmin filtering or CustomerAdmin)
  const filteredRoles = filterTenantId
    ? roles.filter((role) => role.tenantId === filterTenantId)
    : roles;

  return filteredRoles.map((role) => ({
    role: role.role,
    permissions: role.permissions,
    id: role._id,
  }));
};

const RoleManagement = () => {
  const { roles, isLoading, error, fetchRoles, addRole, updateRole, deleteRole } =
    useRoleManagementStore();
  const [apiRoles, setApiRoles] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [newRole, setNewRole] = useState({ role: "", permissions: {}, tenantId: "" });
  const [formError, setFormError] = useState("");
  const [customers, setCustomers] = useState([]); // To store list of customers (organizations)
  const [selectedTenantId, setSelectedTenantId] = useState(""); // For filtering roles by tenantId

  const userRole = localStorage.getItem("role"); // Get user role from localStorage
  const userTenantId = localStorage.getItem("tenantId"); // Get tenantId for CustomerAdmin

  // Fetch roles and customers (for SuperAdmin)
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/customer/getcustomers`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch customers");
        }
        const data = await response.json();
        setCustomers(data.customers || []);
      } catch (error) {
        console.error("Error fetching customers:", error);
        setFormError("Failed to fetch organizations");
      }
    };

    if (userRole === "SuperAdmin") {
      fetchCustomers();
      fetchRoles(); // Fetch all roles for SuperAdmin initially
    } else if (userRole === "CustomerAdmin" && userTenantId) {
      fetchRoles(userTenantId); // Fetch roles for CustomerAdmin's tenantId
      setSelectedTenantId(userTenantId); // Set filter for CustomerAdmin
    }
  }, [fetchRoles, userRole, userTenantId]);

  // Update the displayed roles based on the selected tenantId
  useEffect(() => {
    const filterTenantId = userRole === "CustomerAdmin" ? userTenantId : selectedTenantId;
    setApiRoles(transformedRolesData(roles, filterTenantId));
  }, [roles, selectedTenantId, userRole, userTenantId]);

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    try {
      // For CustomerAdmin, implicitly set tenantId from localStorage
      const submissionData = { ...newRole };
      if (userRole === "CustomerAdmin") {
        const tenantIdFromStorage = localStorage.getItem("tenantId");
        if (!tenantIdFromStorage || tenantIdFromStorage === "null") {
          setFormError("CustomerAdmin must have a tenantId to create roles.");
          return;
        }
        submissionData.tenantId = tenantIdFromStorage;
      } else if (userRole === "SuperAdmin" && !submissionData.tenantId) {
        setFormError("Please select an organization for the role.");
        return;
      }

      if (isEditing) {
        await updateRole(newRole.id, submissionData);
        setIsEditing(false);
      } else {
        await addRole(submissionData);
        setNewRole({ role: "", permissions: {}, tenantId: "" });
      }
      fetchRoles(userRole === "CustomerAdmin" ? userTenantId : undefined);
      setOpen(false);
    } catch (error) {
      setFormError(error.message || "Error saving role");
    }
  };

  const handleEditRole = (role) => {
    setNewRole({
      role: role.role,
      permissions: role.permissions,
      id: role.id,
      tenantId: role.tenantId || (userRole === "CustomerAdmin" ? userTenantId : ""),
    });
    setIsEditing(true);
    setOpen(true);
  };

  const handleDelete = async (roleId) => {
    if (!roleId) {
      setFormError("Role ID is undefined");
      return;
    }
    try {
      await deleteRole(roleId);
      fetchRoles(userRole === "CustomerAdmin" ? userTenantId : undefined);
    } catch (error) {
      setFormError(error.message || "Error deleting role");
    }
  };

  const renderCheckboxes = (params) => {
    const { field } = params.colDef;
    const permissions = params.data?.permissions || {};

    return (
      <div className="flex space-x-2">
        {["create", "read", "update", "delete"].map((action) => (
          <label key={action} className="flex items-center space-x-1">
            <input
              type="checkbox"
              checked={permissions[field]?.[action] || false}
              className="accent-blue-500"
              //disabled // Prevent direct edits in the grid
            />
            <span>{action[0].toUpperCase()}</span>
          </label>
        ))}
      </div>
    );
  };

  const columnDefs = [
    { headerName: "Role Name", field: "role", sortable: true, flex: 1 },
    ...[
      "userManagement",
      "surveyManagement",
      "dashboardView",
      "roadRepairCostEstimation",
    ].map((feature) => ({
      headerName: feature.replace(/([A-Z])/g, " $1"),
      field: feature,
      cellRenderer: renderCheckboxes,
      flex: 2,
    })),
    {
      headerName: "Action",
      field: "action",
      cellRenderer: (params) => (
        <div className="flex space-x-2">
          <button
            className="text-blue-500 hover:text-blue-700"
            onClick={() => handleEditRole(params.data)}
          >
            <FiEdit size={18} />
          </button>
          <button
            className="text-red-500 hover:text-red-700"
            onClick={() => handleDelete(params.data.id)}
          >
            <RiDeleteBin6Line size={18} />
          </button>
        </div>
      ),
      flex: 1,
    },
  ];

  return (
    <div className="mt-20 p-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">ROLE MANAGEMENT</h2>
        <div className="flex items-center gap-4">
          {userRole === "SuperAdmin" && (
            <div className="flex items-center gap-2">
              <label className="font-semibold">Filter by Organization:</label>
              <select
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                className="border border-black p-2 rounded"
              >
                <option value="">All Organizations</option>
                {customers.map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {customer.nameOfOrganization}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            className="flex items-center gap-2 bg-[#a9e5f7] text-[#074659] font-bold px-4 py-2 rounded-full"
            onClick={() => {
              setOpen(true);
              setNewRole({ role: "", permissions: {}, tenantId: userRole === "CustomerAdmin" ? userTenantId : "" });
              setFormError("");
            }}
          >
            <PlusIcon size={30} /> CREATE ROLE
          </button>
        </div>
      </div>
      {error && <div className="text-red-500 mt-2">{error}</div>}
      {apiRoles.length === 0 && !isLoading && !error && (
        <div className="text-gray-500 mt-2">
          No roles found. Click "Create Role" to get started.
        </div>
      )}
      <div
        className="ag-theme-alpine mt-6"
        style={{ height: 400, width: "100%" }}
      >
        <AgGridReact
          rowData={apiRoles}
          rowHeight={50}
          columnDefs={columnDefs}
          rowSelection="multiple"
          loadingOverlayComponent={isLoading ? "Loading..." : null}
        />
      </div>

      <Modal isOpen={open}>
        <form onSubmit={handleRoleSubmit} className="w-full max-w-2xl mx-auto">
          <div className="p-6">
            {formError && <div className="text-red-500 mb-4">{formError}</div>}
            {userRole === "SuperAdmin" && (
              <div className="mb-4">
                <label className="block mb-2 font-semibold">Organization</label>
                <select
                  value={newRole.tenantId}
                  onChange={(e) =>
                    setNewRole({ ...newRole, tenantId: e.target.value })
                  }
                  className="w-full border border-black p-2"
                  required
                >
                  <option value="">Select Organization</option>
                  {customers.map((customer) => (
                    <option key={customer._id} value={customer._id}>
                      {customer.nameOfOrganization}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <label className="block mb-2 font-semibold">Role Name</label>
            <input
              type="text"
              placeholder="Enter Role Name"
              className="w-full border border-black p-2 mb-4"
              value={newRole.role}
              onChange={(e) => setNewRole({ ...newRole, role: e.target.value })}
              required
            />
            <div className="mt-4">
              <h2 className="font-semibold mb-2">Permissions</h2>
              {[
                "userManagement",
                "surveyManagement",
                "dashboardView",
                "roadRepairCostEstimation",
              ].map((feature) => (
                <div
                  key={feature}
                  className="flex items-center justify-between mt-2 gap-5"
                >
                  <span className="font-medium">
                    {feature.replace(/([A-Z])/g, " $1")}
                  </span>
                  <div className="flex space-x-4">
                    {["create", "read", "update", "delete"].map((action) => (
                      <label
                        key={action}
                        className="flex items-center space-x-2"
                      >
                        <input
                          type="checkbox"
                          checked={
                            newRole.permissions[feature]?.[action] || false
                          }
                          onChange={() => {
                            setNewRole((prev) => {
                              const updatedPermissions = {
                                ...prev.permissions,
                                [feature]: {
                                  ...prev.permissions[feature],
                                  [action]:
                                    !prev.permissions[feature]?.[action] ||
                                    false,
                                },
                              };
                              return {
                                ...prev,
                                permissions: updatedPermissions,
                              };
                            });
                          }}
                        />
                        <span>{action}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 bg-[#f5fdff] text-[#ffffff] mt-10 py-3 px-3">
            <button
              className="bg-[#7e90ab] px-5 py-1 rounded-full"
              onClick={() => setOpen(false)}
            >
              CANCEL
            </button>
            <button
              className="bg-[#063269] px-5 py-1 rounded-full"
              type="submit"
            >
              SUBMIT
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default RoleManagement;